import { TeamOutlined } from "@ant-design/icons";
import { Alert, Input, Segmented, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { UserBrief } from "@/entities/user";
import { useUsers } from "@/entities/user";
import { errorMessage } from "@/shared/api";
import { SECTIONS } from "@/shared/config";
import { formatDateTime } from "@/shared/lib";
import {
  EmptyState,
  LoadMore,
  Money,
  PageHeader,
  SectionCard,
  StatusTag,
  UserCell,
} from "@/shared/ui";

/** Значение сегмента → параметр `blocked` запроса. */
const FILTERS = { all: null, active: false, blocked: true } as const;
type FilterKey = keyof typeof FILTERS;

export function UsersPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  // Segmented вместо галочки «только заблокированные»: галочка умела показать
  // либо всех, либо заблокированных, а «только активных» — никак, хотя
  // спрашивают об этом не реже.
  const [filter, setFilter] = useState<FilterKey>("all");
  const query = useUsers(search, FILTERS[filter]);

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<UserBrief> = [
    {
      title: t("common.user"),
      key: "user",
      render: (_, row) => (
        <UserCell
          id={row.id}
          username={row.username}
          firstName={row.first_name}
          telegramId={row.telegram_id}
        />
      ),
    },
    {
      title: t("users.balance"),
      key: "balance",
      align: "right",
      render: (_, row) => <Money value={row.balance} />,
    },
    {
      title: t("common.status"),
      key: "status",
      width: 160,
      // Служебный аккаунт и заблокированный — разные состояния, и показывать
      // их одним тегом нельзя: первый живой пользователь, просто скрытый с
      // витрины. Пустая ячейка у обычного — не заглушка, а отсутствие признака.
      render: (_, row) => (
        <>
          {row.is_blocked ? <StatusTag status="blocked" /> : null}
          {row.is_service ? <Tag>{t("users.service")}</Tag> : null}
          {!row.is_blocked && !row.is_service ? "—" : null}
        </>
      ),
    },
    {
      title: t("users.registered"),
      key: "created_at",
      render: (_, row) => formatDateTime(row.created_at),
    },
  ];

  return (
    <>
      <PageHeader
        section="users"
        title={t("users.title")}
        subtitle={t("users.subtitle")}
        extra={
          <>
            <Segmented<FilterKey>
              value={filter}
              onChange={setFilter}
              options={[
                { value: "all", label: t("users.filterAll") },
                { value: "active", label: t("users.filterActive") },
                { value: "blocked", label: t("users.filterBlocked") },
              ]}
            />
            {/* Search, а не Input: поиск уходит по Enter, а не на каждое нажатие
                — лимит на чтение общий на всех операторов за одним IP. */}
            <Input.Search
              allowClear
              placeholder={t("users.searchPlaceholder")}
              style={{ width: 320 }}
              onSearch={setSearch}
            />
          </>
        }
      />

      {query.isError && (
        <Alert type="error" showIcon title={errorMessage(query.error)} style={{ marginBottom: 16 }} />
      )}

      <SectionCard color={SECTIONS.users.color} bodyPadding={0}>
        <Table<UserBrief>
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={query.isPending}
          pagination={false}
          locale={{
            emptyText: (
              <EmptyState
                icon={<TeamOutlined aria-hidden />}
                title={t("users.empty")}
                hint={t("users.emptyHint")}
              />
            ),
          }}
        />
        <LoadMore
          hasMore={query.hasNextPage}
          loading={query.isFetchingNextPage}
          onClick={() => {
            void query.fetchNextPage();
          }}
        />
      </SectionCard>
    </>
  );
}
